/**
 * v1 cat archetypes — milestone 1 subset.
 *
 * Three cats per docs/PLAN.md §13.1 lineup, marked P0:
 *   1. Tabby (the standard) — *deferred to milestone 2 to keep m1 focused*
 *   2. Loaf (orange)        — wide, squat, stable foundation cat
 *   3. Kitten (gray)        — tiny shim/stabilizer
 *   4. Hefty cat (calico)   — high mass, wide base, foundational
 *
 * Greybox shapes per the placeholder/final matrix in README.md.
 * Numbers are starting points; tunable by editing this file.
 */

import type { CatArchetype } from '@meowntain/core';

export const KITTEN: CatArchetype = {
  id: 'kitten_gray',
  displayName: 'Gray Kitten',
  // Tiny mass — kittens are stabilizers, not foundations.
  massKg: 1.5,
  // Their loadCapacity is small; they get squished easily under heavies.
  loadCapacityKg: 4,
  width: 28,
  height: 26,
  // Soft gray.
  greyboxColor: 0xb8b3c2,
  friction: 0.95,
  restitution: 0.0,
  soothingVerb: 'hold',
  petAffinity: 1.4,
  boredWeights: {
    walk_away: 0.6,
    bat_sleeper: 0.8,
    yowl: 0.4,
    play_toy: 2.2,
    knead_pillow: 1.2,
  },
};

export const LOAF: CatArchetype = {
  id: 'loaf_orange',
  displayName: 'Orange Loaf',
  // A respectable cat at rest.
  massKg: 5.0,
  // Wide base + low slung = good foundation.
  loadCapacityKg: 18,
  width: 64,
  height: 38,
  // Warm orange.
  greyboxColor: 0xe89456,
  friction: 0.9,
  restitution: 0.0,
  soothingVerb: 'stroke',
  petAffinity: 1.0,
  boredWeights: {
    walk_away: 2.0,
    bat_sleeper: 0.5,
    yowl: 0.8,
    play_toy: 0.6,
    knead_pillow: 0.8,
  },
};

export const HEFTY: CatArchetype = {
  id: 'hefty_calico',
  displayName: 'Hefty Calico',
  // The serious one.
  massKg: 7.5,
  // Highest capacity in the milestone-1 set — a true foundation cat.
  loadCapacityKg: 28,
  width: 72,
  height: 56,
  // Tri-color reads as cream — placeholder approximation.
  greyboxColor: 0xc9b690,
  friction: 0.85,
  restitution: 0.0,
  soothingVerb: 'stroke',
  petAffinity: 0.85,
  boredWeights: {
    walk_away: 0.7,
    bat_sleeper: 0.9,
    yowl: 1.4,
    play_toy: 0.5,
    knead_pillow: 1.8,
  },
};

/** All milestone-1 cat archetypes, in spawn-rotation order. */
export const M1_ARCHETYPES: readonly CatArchetype[] = [LOAF, KITTEN, HEFTY] as const;

/**
 * Look up a cat archetype by id. Throws on unknown id — callers should
 * validate at the manifest boundary.
 */
export function getArchetype(id: string): CatArchetype {
  const found = M1_ARCHETYPES.find((a) => a.id === id);
  if (!found) throw new Error(`Unknown cat archetype: ${id}`);
  return found;
}
